import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { PortalApiError } from "@/lib/portalApi";

// ============================================================================
// Server-Side Pagination (with fetchFn)
// ============================================================================

export interface PaginatedResponse<T> {
  data: T[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface ServerSortState {
  key: string;
  direction: "asc" | "desc";
}

export interface UseServerPaginatedDataOptions<T> {
  fetchFn: (
    page: number,
    pageSize: number,
    sort?: ServerSortState,
  ) => Promise<PaginatedResponse<T>>;
  initialPageSize?: number;
  dependencies?: unknown[];
  /**
   * Opt-in server-side sorting. When set, the current sort is passed to
   * fetchFn so ordering happens across the WHOLE dataset (server ORDER BY
   * before OFFSET/LIMIT) — never sort the returned page client-side, that
   * only reorders the visible rows.
   */
  initialSort?: ServerSortState;
  /**
   * A total the caller learns separately from the page fetch (e.g. a slower
   * count endpoint). When set it replaces the fetched `totalCount` for the
   * pager — never below the rows actually on screen, so a stale cached count
   * cannot hide the pager under a page of rows. `undefined` (the default)
   * keeps the fetched one.
   */
  totalCountOverride?: number;
}

export interface UseServerPaginatedDataReturn<T> {
  data: T[];
  isLoading: boolean;
  error: string | null;
  /** True when the fetch returned HTTP 403 (permission denied) */
  isPermissionDenied: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  pageSizeOptions: number[];
  goToPage: (page: number) => void;
  nextPage: () => void;
  prevPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
  setPageSize: (size: number) => void;
  refresh: () => void;
  canGoNext: boolean;
  canGoPrev: boolean;
  startIndex: number;
  endIndex: number;
  /** Current server sort (null when sorting is not enabled/active). */
  sort: ServerSortState | null;
  /** Toggle/set the sort column — same semantics as useSortableData. */
  requestSort: (key: string) => void;
  /** Direction for a column header — plugs into SortableTableHead. */
  getSortDirection: (key: string) => "asc" | "desc" | null;
}

/**
 * Hook for server-side pagination with async data fetching
 */
export function usePaginatedData<T>({
  fetchFn,
  initialPageSize = 25,
  dependencies = [],
  initialSort,
  totalCountOverride,
}: UseServerPaginatedDataOptions<T>): UseServerPaginatedDataReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPermissionDenied, setIsPermissionDenied] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [fetchedTotalCount, setTotalCount] = useState(0);
  // Floored at the rows on screen — but only once they belong to this page:
  // mid-fetch `page` has moved and `data` is still the previous page's.
  const totalCount =
    totalCountOverride === undefined
      ? fetchedTotalCount
      : isLoading
        ? totalCountOverride
        : Math.max(totalCountOverride, (page - 1) * pageSize + data.length);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sort, setSort] = useState<ServerSortState | null>(initialSort ?? null);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  const prevDepsRef = useRef<string>("");

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [totalCount, pageSize],
  );

  // Serialize dependencies for comparison
  const depsString = JSON.stringify(dependencies);

  // Reset page to 1 when dependencies change (not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevDepsRef.current = depsString;
      return;
    }

    if (prevDepsRef.current !== depsString) {
      prevDepsRef.current = depsString;
      setPage(1);
    }
  }, [depsString]);

  // Main fetch effect
  useEffect(() => {
    let cancelled = false;

    const doFetch = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetchFn(page, pageSize, sort ?? undefined);
        if (!cancelled) {
          setData(response.data);
          setTotalCount(response.totalCount);
        }
      } catch (err) {
        if (!cancelled) {
          const is403 =
            err instanceof PortalApiError && err.isPermissionDenied;
          setIsPermissionDenied(is403);
          if (!is403) {
            console.error("Failed to fetch paginated data:", err);
          }
          setError(err instanceof Error ? err.message : "Failed to fetch data");
          setData([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [fetchFn, page, pageSize, refreshKey, sort]);

  const requestSort = useCallback((key: string) => {
    setSort((current) => {
      // Same column: toggle direction. New column: start ascending.
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
    setPage(1); // A new ordering invalidates the current page position
  }, []);

  const getSortDirection = useCallback(
    (key: string): "asc" | "desc" | null =>
      sort?.key === key ? sort.direction : null,
    [sort],
  );

  const goToPage = useCallback(
    (newPage: number) => {
      const validPage = Math.max(1, Math.min(newPage, totalPages));
      setPage(validPage);
    },
    [totalPages],
  );

  const nextPage = useCallback(() => {
    if (page < totalPages) setPage((p) => p + 1);
  }, [page, totalPages]);

  const prevPage = useCallback(() => {
    if (page > 1) setPage((p) => p - 1);
  }, [page]);

  const firstPage = useCallback(() => setPage(1), []);

  const lastPage = useCallback(() => setPage(totalPages), [totalPages]);

  const setPageSize = useCallback((size: number) => {
    setPageSizeState(size);
    setPage(1); // Reset to first page when changing page size
  }, []);

  const refresh = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
  }, []);

  const startIndex = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, totalCount);

  return {
    data,
    isLoading,
    error,
    isPermissionDenied,
    pagination: {
      page,
      pageSize,
      totalCount,
      totalPages,
    },
    pageSizeOptions: [10, 25, 50, 100],
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext: page < totalPages,
    canGoPrev: page > 1,
    startIndex,
    endIndex,
    sort,
    requestSort,
    getSortDirection,
  };
}

// ============================================================================
// Client-Side Pagination (with data array)
// ============================================================================

export interface UseClientPaginatedDataOptions<T> {
  data: T[];
  itemsPerPage?: number;
  searchFields?: (keyof T)[];
  searchTerm?: string;
}

export interface UseClientPaginatedDataReturn<T> {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (items: number) => void;
  paginatedData: T[];
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
}

/**
 * Hook for client-side pagination and search filtering
 */
export function useClientPaginatedData<T extends Record<string, unknown>>({
  data,
  itemsPerPage: initialItemsPerPage = 25,
  searchFields = [],
  searchTerm = "",
}: UseClientPaginatedDataOptions<T>): UseClientPaginatedDataReturn<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(initialItemsPerPage);

  // Filter data based on search term
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchTerm || searchFields.length === 0) {
      return data;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    return data.filter((item) =>
      searchFields.some((field) => {
        const value = item[field];
        if (value == null) return false;
        return String(value).toLowerCase().includes(lowerSearchTerm);
      }),
    );
  }, [data, searchTerm, searchFields]);

  // Calculate pagination
  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  // Reset to page 1 if current page exceeds total pages
  const validCurrentPage = currentPage > totalPages ? 1 : currentPage;

  // Get paginated data
  const startIndex = (validCurrentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  return {
    currentPage: validCurrentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage: (items) => {
      setItemsPerPage(items);
      setCurrentPage(1); // Reset to first page when changing items per page
    },
    paginatedData,
    totalPages,
    totalItems,
    startIndex: totalItems === 0 ? 0 : startIndex + 1, // 1-indexed for display
    endIndex,
  };
}
